/**
 * PV installations and batteries (RGIE / AREI, decision 0017). All `verified: false`: drawn
 * from installer guides and inspection-body remarks, to be confirmed against the AREI text.
 * - R-PV-01: the inverter on a circuit of its own;
 * - R-PV-02: the RCD of the inverter circuit (type B unless the inverter's manual allows A);
 * - R-PV-03: a DC isolator between the strings and the inverter;
 * - R-PV-04: the DC isolator rated for the strings' open-circuit voltage;
 * - R-PV-05: the DC side drawn with its inverter;
 * - R-PV-06: the warning labels (installation PV, several power sources; the battery).
 */
import { effectiveRcd, type CircuitInfo } from '@elec/analysis';
import { name, rule, type Rule } from './core.ts';

const has = (c: CircuitInfo, id: string) => c.points.some((p) => p.element.symbolId === id);
const PV_IDS = new Set(['iec.pv-string', 'iec.pv-array', 'iec.inverter', 'iec.inverter-dc']);

export const PV_RULES: Rule[] = [
  rule(
    {
      id: 'R-PV-01',
      severity: 'warning',
      title: {
        fr: 'Onduleur PV sur un circuit dédié',
        en: 'PV inverter on a dedicated circuit',
        nl: 'PV-omvormer op een eigen kring',
        de: 'PV-Wechselrichter an einem eigenen Stromkreis',
      },
      why: {
        fr: 'L’onduleur réinjecte du courant : son circuit ne doit alimenter rien d’autre, pour pouvoir le couper seul et pour que sa protection reste dimensionnée pour lui.',
        en: 'The inverter feeds current back: its circuit must supply nothing else, so it can be switched off on its own and its protection stays sized for it.',
        nl: 'De omvormer voedt stroom terug: zijn kring mag niets anders voeden, zodat hij alleen kan worden uitgeschakeld en zijn beveiliging erop afgestemd blijft.',
        de: 'Der Wechselrichter speist Strom zurück: Sein Stromkreis darf nichts anderes versorgen, damit er allein abgeschaltet werden kann und seine Schutzeinrichtung auf ihn ausgelegt bleibt.',
      },
      fix: {
        fr: 'Placez l’onduleur seul sur son circuit (Schéma → Installation photovoltaïque… en crée un).',
        en: 'Put the inverter alone on its circuit (Diagram → PV installation… creates one).',
        nl: 'Zet de omvormer alleen op zijn kring (Schema → Fotovoltaïsche installatie… maakt er een).',
        de: 'Setzen Sie den Wechselrichter allein an seinen Stromkreis (Schema → Photovoltaikanlage… legt einen an).',
      },
      verified: false,
    },
    ({ a }) =>
      a.circuits
        .filter((c) => has(c, 'iec.inverter') && c.points.length > 1)
        .map((c) => ({
          targets: [c.device.id],
          sheetId: c.sheet.id,
          message: {
            fr: `Circuit ${name(c)} : l’onduleur partage le circuit avec ${c.points.length - 1} autre(s) point(s)`,
            en: `Circuit ${name(c)}: the inverter shares the circuit with ${c.points.length - 1} other point(s)`,
            nl: `Kring ${name(c)}: de omvormer deelt de kring met ${c.points.length - 1} ander(e) punt(en)`,
            de: `Stromkreis ${name(c)}: der Wechselrichter teilt den Stromkreis mit ${c.points.length - 1} weiteren Punkt(en)`,
          },
        })),
  ),
  rule(
    {
      id: 'R-PV-02',
      severity: 'info',
      title: {
        fr: 'Différentiel du circuit de l’onduleur',
        en: 'RCD of the inverter circuit',
        nl: 'Differentieel van de omvormerkring',
        de: 'FI-Schutzschalter des Wechselrichter-Stromkreises',
      },
      why: {
        fr: 'Un onduleur sans transformateur peut produire un courant de défaut continu qui aveugle un différentiel de type A. Un type B est requis, sauf si la notice de l’onduleur déclare qu’il n’en produit pas.',
        en: 'A transformerless inverter can produce a DC fault current that blinds a type A RCD. A type B is required unless the inverter’s manual states that it produces none.',
        nl: 'Een omvormer zonder transformator kan een gelijkstroomfout veroorzaken die een differentieel type A verblindt. Een type B is vereist, tenzij de handleiding van de omvormer verklaart dat hij er geen veroorzaakt.',
        de: 'Ein transformatorloser Wechselrichter kann einen Gleichfehlerstrom erzeugen, der einen FI Typ A blind macht. Ein Typ B ist erforderlich, es sei denn, die Anleitung des Wechselrichters erklärt, dass er keinen erzeugt.',
      },
      fix: {
        fr: 'Vérifiez la notice : type B, ou type A si le fabricant l’autorise (gardez la page de la notice pour l’organisme).',
        en: 'Check the manual: type B, or type A if the manufacturer allows it (keep the page of the manual for the inspection body).',
        nl: 'Kijk de handleiding na: type B, of type A als de fabrikant het toelaat (bewaar de bladzijde voor het keuringsorganisme).',
        de: 'Prüfen Sie die Anleitung: Typ B, oder Typ A, wenn der Hersteller es erlaubt (bewahren Sie die Seite für die Prüfstelle auf).',
      },
      verified: false,
    },
    ({ a }) =>
      a.circuits.flatMap((c) => {
        if (!has(c, 'iec.inverter')) return [];
        const r = effectiveRcd(c);
        if (!r || r.type === 'B') return [];
        return [
          {
            severity: r.type === 'AC' ? ('warning' as const) : ('info' as const),
            targets: [r.element.id],
            sheetId: r.element.sheetId,
            message: {
              fr: `Circuit ${name(c)} (onduleur) : différentiel type ${r.type} — type B sauf indication contraire du fabricant`,
              en: `Circuit ${name(c)} (inverter): RCD type ${r.type} — type B unless the manufacturer says otherwise`,
              nl: `Kring ${name(c)} (omvormer): differentieel type ${r.type} — type B tenzij de fabrikant anders aangeeft`,
              de: `Stromkreis ${name(c)} (Wechselrichter): FI Typ ${r.type} — Typ B, sofern der Hersteller nichts anderes angibt`,
            },
          },
        ];
      }),
  ),
  rule(
    {
      id: 'R-PV-03',
      severity: 'warning',
      title: {
        fr: 'Sectionneur DC entre panneaux et onduleur',
        en: 'DC isolator between panels and inverter',
        nl: 'DC-scheider tussen panelen en omvormer',
        de: 'DC-Trennschalter zwischen Modulen und Wechselrichter',
      },
      why: {
        fr: 'Les panneaux restent sous tension tant qu’ils sont éclairés : un sectionneur DC (intégré à l’onduleur ou séparé) doit permettre d’isoler l’onduleur côté continu.',
        en: 'The panels stay live as long as there is light: a DC isolator (built into the inverter or separate) must isolate the inverter on the DC side.',
        nl: 'De panelen blijven onder spanning zolang er licht is: een DC-scheider (in de omvormer of apart) moet de omvormer aan de gelijkstroomzijde kunnen scheiden.',
        de: 'Die Module stehen unter Spannung, solange Licht einfällt: Ein DC-Trennschalter (im Wechselrichter oder separat) muss den Wechselrichter gleichstromseitig trennen.',
      },
      fix: {
        fr: 'Dessinez le sectionneur DC entre chaque chaîne et l’onduleur (ou notez qu’il est intégré).',
        en: 'Draw the DC isolator between each string and the inverter (or note that it is built in).',
        nl: 'Teken de DC-scheider tussen elke string en de omvormer (of noteer dat hij ingebouwd is).',
        de: 'Zeichnen Sie den DC-Trennschalter zwischen jedem Strang und dem Wechselrichter (oder vermerken Sie, dass er eingebaut ist).',
      },
      verified: false,
    },
    ({ project }) => {
      const els = Object.values(project.elements);
      const strings = els.filter((e) => e.symbolId === 'iec.pv-string');
      if (!strings.length || els.some((e) => e.symbolId === 'iec.dc-isolator')) return [];
      return [
        {
          targets: strings.map((e) => e.id),
          sheetId: strings[0]!.sheetId,
          message: {
            fr: 'Côté DC : aucun sectionneur DC dessiné',
            en: 'DC side: no DC isolator drawn',
            nl: 'DC-zijde: geen DC-scheider getekend',
            de: 'DC-Seite: kein DC-Trennschalter gezeichnet',
          },
        },
      ];
    },
  ),
  rule(
    {
      id: 'R-PV-04',
      severity: 'error',
      title: {
        fr: 'Tension du sectionneur DC',
        en: 'DC isolator voltage',
        nl: 'Spanning van de DC-scheider',
        de: 'Spannung des DC-Trennschalters',
      },
      why: {
        fr: 'Le sectionneur doit couper la tension à vide de la chaîne, la plus haute par temps froid.',
        en: 'The isolator must break the string’s open-circuit voltage, highest in cold weather.',
        nl: 'De scheider moet de nullastspanning van de string onderbreken, die bij koud weer het hoogst is.',
        de: 'Der Trennschalter muss die Leerlaufspannung des Strangs trennen, die bei Kälte am höchsten ist.',
      },
      fix: {
        fr: 'Choisissez un sectionneur DC de tension assignée au moins égale (avec la marge du froid du fabricant des panneaux).',
        en: 'Choose a DC isolator rated for at least that voltage (with the panel maker’s cold-weather margin).',
        nl: 'Kies een DC-scheider met een nominale spanning die minstens even hoog is (met de koudemarge van de panelenfabrikant).',
        de: 'Wählen Sie einen DC-Trennschalter mit mindestens dieser Bemessungsspannung (mit der Kältereserve des Modulherstellers).',
      },
      verified: false,
    },
    ({ project }) => {
      const els = Object.values(project.elements);
      const voc = Math.max(
        0,
        ...els.filter((e) => e.symbolId === 'iec.pv-string').map((e) => Number(e.props.vocV) || 0),
      );
      if (!voc) return [];
      return els
        .filter(
          (e) =>
            e.symbolId === 'iec.dc-isolator' &&
            Number(e.props.voltageV) > 0 &&
            Number(e.props.voltageV) < voc,
        )
        .map((e) => ({
          targets: [e.id],
          sheetId: e.sheetId,
          message: {
            fr: `${e.tag} : ${e.props.voltageV} V DC, sous la tension à vide des chaînes (${voc} V)`,
            en: `${e.tag}: ${e.props.voltageV} V DC, below the strings’ open-circuit voltage (${voc} V)`,
            nl: `${e.tag}: ${e.props.voltageV} V DC, onder de nullastspanning van de strings (${voc} V)`,
            de: `${e.tag}: ${e.props.voltageV} V DC, unter der Leerlaufspannung der Stränge (${voc} V)`,
          },
        }));
    },
  ),
  rule(
    {
      id: 'R-PV-05',
      severity: 'warning',
      title: {
        fr: 'Onduleur raccordé au tableau',
        en: 'Inverter connected to the board',
        nl: 'Omvormer aangesloten op het bord',
        de: 'Wechselrichter am Verteiler angeschlossen',
      },
      why: {
        fr: 'Un côté DC dessiné doit mener à un onduleur, lui-même sur un circuit protégé du tableau.',
        en: 'A drawn DC side must lead to an inverter, itself on a protected circuit of the board.',
        nl: 'Een getekende DC-zijde moet leiden naar een omvormer, zelf op een beveiligde kring van het bord.',
        de: 'Eine gezeichnete DC-Seite muss zu einem Wechselrichter führen, der an einem geschützten Stromkreis des Verteilers liegt.',
      },
      fix: {
        fr: 'Ajoutez le circuit de l’onduleur au tableau, et indiquez son repère dans l’onduleur côté DC.',
        en: 'Add the inverter circuit to the board, and give its tag in the DC-side inverter.',
        nl: 'Voeg de omvormerkring toe aan het bord en vul zijn kenmerk in bij de omvormer aan DC-zijde.',
        de: 'Fügen Sie den Stromkreis des Wechselrichters dem Verteiler hinzu und tragen Sie sein Kennzeichen beim DC-seitigen Wechselrichter ein.',
      },
      verified: false,
    },
    ({ project, a }) => {
      const els = Object.values(project.elements);
      const dcSide = els.filter((e) => e.symbolId === 'iec.pv-string' || e.symbolId === 'iec.inverter-dc');
      if (!dcSide.length) return [];
      const acTags = new Set(
        a.circuits.flatMap((c) =>
          c.points.filter((p) => p.element.symbolId === 'iec.inverter').map((p) => p.element.tag),
        ),
      );
      const dcInverters = els.filter((e) => e.symbolId === 'iec.inverter-dc');
      const lost = dcInverters.filter((e) => !acTags.has(String(e.props.acRef ?? '')));
      if (dcInverters.length && !lost.length) return [];
      const target = lost[0] ?? dcSide[0]!;
      return [
        {
          targets: (lost.length ? lost : dcSide).map((e) => e.id),
          sheetId: target.sheetId,
          message: dcInverters.length
            ? {
                fr: `${target.tag} : aucun onduleur « ${target.props.acRef || '?'} » sur un circuit du tableau`,
                en: `${target.tag}: no inverter “${target.props.acRef || '?'}” on a circuit of the board`,
                nl: `${target.tag}: geen omvormer “${target.props.acRef || '?'}” op een kring van het bord`,
                de: `${target.tag}: kein Wechselrichter „${target.props.acRef || '?'}“ an einem Stromkreis des Verteilers`,
              }
            : {
                fr: 'Côté DC : les chaînes ne mènent à aucun onduleur',
                en: 'DC side: the strings lead to no inverter',
                nl: 'DC-zijde: de strings leiden naar geen omvormer',
                de: 'DC-Seite: die Stränge führen zu keinem Wechselrichter',
              },
        },
      ];
    },
  ),
  rule(
    {
      id: 'R-PV-06',
      severity: 'info',
      title: {
        fr: 'Étiquettes d’une installation PV',
        en: 'Labels of a PV installation',
        nl: 'Etiketten van een PV-installatie',
        de: 'Kennzeichnung einer PV-Anlage',
      },
      why: {
        fr: 'Les intervenants doivent savoir qu’il y a deux sources de tension : au tableau, au compteur et à l’onduleur, une étiquette « installation photovoltaïque » ; au sectionneur DC, que le côté continu reste sous tension ; avec une batterie, une source de plus.',
        en: 'Anyone working on it must know there are two power sources: at the board, the meter and the inverter, a “photovoltaic installation” label; at the DC isolator, that the DC side stays live; with a battery, one more source.',
        nl: 'Wie eraan werkt, moet weten dat er twee spanningsbronnen zijn: aan het bord, de meter en de omvormer een etiket “fotovoltaïsche installatie”; aan de DC-scheider dat de gelijkstroomzijde onder spanning blijft; met een batterij een bron meer.',
        de: 'Wer daran arbeitet, muss wissen, dass es zwei Spannungsquellen gibt: am Verteiler, am Zähler und am Wechselrichter ein Schild „Photovoltaikanlage“; am DC-Trennschalter, dass die Gleichstromseite unter Spannung bleibt; mit einer Batterie eine Quelle mehr.',
      },
      fix: {
        fr: 'Imprimez les étiquettes (Rapports → Étiquettes de l’installation PV) et collez-les.',
        en: 'Print the labels (Reports → PV installation labels) and stick them on.',
        nl: 'Druk de etiketten af (Rapporten → Etiketten van de PV-installatie) en kleef ze op.',
        de: 'Drucken Sie die Schilder (Berichte → Schilder der PV-Anlage) und bringen Sie sie an.',
      },
      verified: false,
    },
    ({ project }) => {
      const els = Object.values(project.elements);
      const pv = els.filter((e) => PV_IDS.has(e.symbolId));
      if (!pv.length) return [];
      const battery = els.some((e) => e.symbolId === 'iec.battery');
      return [
        {
          targets: [],
          sheetId: pv[0]!.sheetId,
          message: battery
            ? {
                fr: 'Installation PV avec batterie : étiquettes « deux sources » et batterie à poser',
                en: 'PV installation with battery: “two sources” and battery labels to fit',
                nl: 'PV-installatie met batterij: etiketten “twee bronnen” en batterij aan te brengen',
                de: 'PV-Anlage mit Batterie: Schilder „zwei Quellen“ und Batterie anzubringen',
              }
            : {
                fr: 'Installation PV : étiquettes « deux sources de tension » à poser',
                en: 'PV installation: “two power sources” labels to fit',
                nl: 'PV-installatie: etiketten “twee spanningsbronnen” aan te brengen',
                de: 'PV-Anlage: Schilder „zwei Spannungsquellen“ anzubringen',
              },
        },
      ];
    },
  ),
];
